import { hasPermission } from '@fc/core'
import { Avatar, AvatarFallback, Badge, Button, cn } from '@fc/ui'
import { ChevronsUpDown, Menu, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { NavLink, Outlet } from 'react-router-dom'
import { BrandLockup } from '../components/brand'
import { ThemeToggle } from '../components/theme-toggle'
import { usePermissions } from '../lib/session'
import { navSections } from './nav'

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const permissions = usePermissions()

  // Client disclosure layer: hide nav items the principal lacks the permission
  // for, then drop any section left with no visible items. Items without a
  // `permission` are always shown. The API still enforces authz server-side.
  const sections = useMemo(
    () =>
      navSections
        .map((section) => ({
          ...section,
          items: section.items.filter(
            (item) => !item.permission || hasPermission(permissions, item.permission),
          ),
        }))
        .filter((section) => section.items.length > 0),
    [permissions],
  )

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center px-5">
        <NavLink to="/app" onClick={onNavigate}>
          <BrandLockup />
        </NavLink>
      </div>

      {/* Org / site switcher (wired to API later) */}
      <div className="px-3">
        <button
          type="button"
          className="flex w-full items-center gap-3 rounded-lg border border-border bg-card px-3 py-2 text-left transition-colors hover:bg-card-hover"
        >
          <div className="grid size-8 place-items-center rounded-md bg-brand-grad text-primary-fg font-display text-sm font-bold">
            GC
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">Grace Community</div>
            <div className="truncate text-xs text-dim">All campuses</div>
          </div>
          <ChevronsUpDown className="size-4 text-dim" />
        </button>
      </div>

      <nav className="mt-4 flex-1 space-y-6 overflow-y-auto px-3 pb-6" aria-label="Primary">
        {sections.map((section) => (
          <div key={section.title}>
            <div className="px-2 pb-2 text-[11px] font-semibold uppercase tracking-wider text-dim">
              {section.title}
            </div>
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/app'}
                    onClick={onNavigate}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-card-hover text-fg shadow-[inset_2px_0_0_0_var(--primary)]'
                          : 'text-muted hover:bg-card-hover hover:text-fg',
                      )
                    }
                  >
                    <item.icon className="size-4 shrink-0" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>

      <div className="border-t border-border p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-1.5">
          <Avatar>
            <AvatarFallback>DA</AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">Demo Admin</div>
            <div className="truncate text-xs text-dim">admin@grace.church</div>
          </div>
          <Badge variant="primary">Owner</Badge>
        </div>
      </div>
    </div>
  )
}

export function AppShell() {
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <div className="min-h-dvh bg-bg">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-border bg-bg-alt lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-[var(--overlay)] backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />
          <aside className="absolute inset-y-0 left-0 w-72 border-r border-border bg-bg-alt shadow-pop">
            <div className="absolute right-3 top-3">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Close menu"
                onClick={() => setMobileOpen(false)}
              >
                <X />
              </Button>
            </div>
            <SidebarContent onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main column */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-[color-mix(in_srgb,var(--bg)_80%,transparent)] px-4 backdrop-blur-md">
          <Button
            variant="ghost"
            size="icon-sm"
            className="lg:hidden"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
          >
            <Menu />
          </Button>

          <label className="relative hidden max-w-md flex-1 items-center sm:flex">
            <Search className="pointer-events-none absolute left-3 size-4 text-dim" />
            <input
              type="search"
              placeholder="Search people, groups, events…"
              className="h-9 w-full rounded-lg border border-border bg-card pl-9 pr-3 text-sm text-fg placeholder:text-dim focus-visible:border-secondary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring"
            />
          </label>

          <div className="ml-auto flex items-center gap-1.5">
            <ThemeToggle />
            <Avatar className="size-8">
              <AvatarFallback>DA</AvatarFallback>
            </Avatar>
          </div>
        </header>

        <main className="mx-auto w-full max-w-[1400px] p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
